import React from 'react';
import Avatar from '../common/Avatar';
import { Document } from '../../types';

interface DocumentsTableProps {
  documents?: Document[];
  onRowClick?: (documentId: string) => void;
  onEdit?: (documentId: string) => void;
  onDelete?: (documentId: string) => void;
}

const mockDocuments: Document[] = [
  {
    id: '1',
    name: 'Merger Agreement Draft.pdf',
    type: 'Contract',
    caseId: '2',
    caseName: 'Merger Agreement Review',
    uploadedBy: {
      id: 'user2',
      username: 'sarah.jones',
      email: 'sarah@example.com',
      initials: 'SJ',
    },
    uploadedAt: '2024-01-09T14:20:00Z',
    size: '2.4 MB',
  },
  {
    id: '2',
    name: 'Client Meeting Notes.docx',
    type: 'Notes',
    caseId: '1',
    caseName: 'Contract Dispute - Tech Corp',
    uploadedBy: {
      id: 'user1',
      username: 'john.smith',
      email: 'john@example.com',
      initials: 'JS',
    },
    uploadedAt: '2024-01-10T11:00:00Z',
    size: '156 KB',
  },
  {
    id: '3',
    name: 'Due Diligence Checklist.xlsx',
    type: 'Template',
    uploadedBy: {
      id: 'user3',
      username: 'mike.wilson',
      email: 'mike@example.com',
      initials: 'MW',
    },
    uploadedAt: '2024-01-08T09:15:00Z',
    size: '89 KB',
  },
];

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents = mockDocuments,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const getFileIcon = (type: string) => {
    if (type.toLowerCase().includes('pdf')) return 'fas fa-file-pdf text-danger';
    if (type.toLowerCase().includes('doc')) return 'fas fa-file-word text-primary-700';
    if (type.toLowerCase().includes('xls')) return 'fas fa-file-excel text-primary-800';
    return 'fas fa-file text-gray-500';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="dashboard-card rounded-2xl overflow-hidden">
      <div className="p-4 pb-0 mb-0 bg-transparent border-b-0 rounded-t-2xl">
        <h6 className="mb-0 font-semibold leading-normal text-lg text-slate-700 dark:text-slate-200">Documents</h6>
      </div>
      <div className="flex-auto p-4">
        <div className="overflow-x-auto">
          <table className="table-auto w-full align-middle mb-0">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Name</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90 ps-2">Type</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Case</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Uploaded By</th>
                <th className="px-4 py-3 text-center align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Date</th>
                <th className="px-4 py-3 text-center align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Size</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-600/50">
              {documents.map((document) => (
                <tr
                  key={document.id}
                  className="align-middle even:bg-slate-50 dark:even:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer transition-colors"
                  onClick={() => onRowClick?.(document.id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <div className="flex items-center">
                      <i className={`${getFileIcon(document.name)} mr-3 text-sm`}></i>
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{document.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-slate-200/50 text-slate-600 dark:bg-slate-500/30 dark:text-slate-300">
                      {document.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-0">{document.caseName || '—'}</p>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <div className="flex items-center">
                      <Avatar user={document.uploadedBy} size="small" className="mr-3" />
                      <div className="flex flex-col justify-center">
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{document.uploadedBy.username}</span>
                        {document.uploadedBy.email && <span className="text-xs text-slate-500 dark:text-slate-400">{document.uploadedBy.email}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{formatDate(document.uploadedAt)}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                    <span className="text-xs text-slate-500 dark:text-slate-400">{document.size || '—'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => onEdit?.(document.id)} className="text-slate-500 font-bold text-xs hover:text-slate-700 dark:hover:text-slate-200" title="Edit user">Edit</button>
                      <button onClick={() => onDelete?.(document.id)} className="text-slate-500 font-bold text-xs hover:text-danger" title="Delete">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DocumentsTable;
